import classNames from 'classnames';
import React, { CSSProperties, forwardRef } from 'react';

import styles from './SelectMenu.module.css';
import { Icon } from '../Icon';
import { Menu } from '../Menu';

export type SelectMenuProps = {
  /**
   * Aria label for the select. If unset, uses the text.
   */
  ariaLabel?: string;
  /**
   * Children to display in the dropdown. Use `MenuItem` for intended styling.
   */
  children: React.ReactNode;
  /**
   * Whether the select is disabled.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * Widen the dropdown if it's less wide than the select.
   * @default false
   */
  fillDropdown?: boolean;
  /**
   * Whether the accompanying menu items should have space for icons.
   * @default false
   */
  hasIcons?: boolean;
  /**
   * Icon to display in the select.
   */
  icon?: string;
  /**
   * Unique identifier for the select.
   */
  id?: string;
  /**
   * OnClick handler for the popover.
   */
  onClick: () => void;
  /**
   * OnClose handler for the popover.
   */
  onClose: () => void;
  /**
   * Whether the dropdown is open.
   */
  open: boolean;
  /**
   * Optional inline styles to add to the container element.
   * Do not alter the look of the component! Should be used only for sizing and positioning.
   */
  style?: CSSProperties;
  /**
   * Text to display in the select.
   */
  text: string;
  /**
   * Tooltip text to display on hover.
   */
  tooltip?: string;
};

/*
 * Popover with a select button and a dropdown menu. Use `MenuItem` as children for intended styling.
 */
export const SelectMenu = forwardRef<HTMLDivElement, SelectMenuProps>(
  (
    {
      ariaLabel,
      children,
      disabled = false,
      fillDropdown = false,
      hasIcons = false,
      icon,
      id,
      onClick,
      onClose,
      open,
      style,
      text,
      tooltip,
    }: SelectMenuProps,
    ref?,
  ) => {
    const onKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        event.preventDefault();

        onClose();
      }
    };

    return (
      <div
        className={classNames('fd-popover', styles['sba-popover'])}
        ref={ref}
      >
        <div
          aria-disabled={disabled}
          aria-expanded={open}
          aria-haspopup="true"
          className="fd-popover__control"
        >
          <div
            className={classNames('fd-select', styles['select-main-container'])}
            style={style}
          >
            <button
              aria-disabled={disabled}
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-label={`${ariaLabel || text}`}
              className="fd-select__control"
              id={id}
              onClick={onClick}
              onKeyDown={onKeyDown}
              tabIndex={0}
              title={tooltip || text}
              type="button"
            >
              {icon && (
                <div className={styles['select-icon-container']}>
                  <Icon name={icon} />
                </div>
              )}
              <span
                className={classNames(
                  'fd-select__text-content',
                  icon && styles['select-icon-text-container'],
                )}
              >
                {text}
              </span>
              <span className="fd-button fd-button--transparent fd-select__button">
                <Icon name="slim-arrow-down" />
              </span>
            </button>
          </div>
        </div>
        <div
          aria-hidden={!open}
          className={classNames(
            'fd-popover__body',
            'fd-popover__body--no-arrow',
            'fd-popover__body--dropdown',
            styles['popover-container'],
            fillDropdown && styles['fill-dropdown'],
          )}
        >
          <div className="fd-popover__wrapper">
            <Menu
              hasIcons={hasIcons}
              onClose={onClose}
              open={open}
              style={{ minWidth: fillDropdown ? '100%' : 'auto' }}
            >
              {children}
            </Menu>
          </div>
        </div>
      </div>
    );
  },
);
